import dayjs from 'dayjs'
import api from '@/api'
import { nextWeekdayAfter } from '@/helpers/dateHelper'
import { _surfaceErrorMessage, _surfaceSuccessMessage } from '@/snackbar'
import {
  EMPLOYEE_TYPE_PARTNER,
  EMPLOYEE_TYPE_EMPLOYEE,
  ENTITIES,
  CONSULTANT_TIERS,
} from '../constants'
import { onAClientOrLastAvailableDayFilter, lastAvailableDayWithClient } from '../helpers'

const GRANULARITY_OF_LAST_AVAILABLE_DAY = 'day'

export const getters = {
  getEntity: (state) => (entityName) => state[entityName],

  getEntityById() {
    return (entityName, id) => {
      if (this[entityName]?.length) {
        return this[entityName].find((entity) => entity.id === id)
      }
      return this[entityName]?.[id]
    }
  },

  getNextAvailableDateForUser() {
    return (userId) => {
      const { currentStaffingRoles, lastAvailableDay } = this.getEntityById(ENTITIES.USERS, userId)
      const endDate = currentStaffingRoles?.map((sr) => sr.endDate)[0]
      return endDate
        ? lastAvailableDay
          ? lastAvailableDayWithClient(lastAvailableDay, endDate)
          : nextWeekdayAfter(endDate)
        : dayjs().toDate()
    }
  },

  activeUsers(state) {
    const users = {}
    for (const id in state.users) {
      const user = state.users[id]
      if (user.active) users[id] = user
    }
    return users
  },

  archivedUsers(state) {
    const users = {}
    for (const id in state.users) {
      const user = state.users[id]
      if (user.active === false) users[id] = user
    }
    return users
  },

  consultants() {
    const users = {}
    for (const id in this.activeUsers) {
      const user = this.activeUsers[id]
      if (
        CONSULTANT_TIERS.has(user.tier) &&
        (!user.lastAvailableDay ||
          dayjs().isSameOrBefore(user.lastAvailableDay, GRANULARITY_OF_LAST_AVAILABLE_DAY))
      ) {
        users[id] = user
      }
    }
    return users
  },

  consultantsWithNextAvailableDate() {
    return Object.values(this.consultants).map((consultant) => ({
      ...consultant,
      nextAvailableDate: this.getNextAvailableDateForUser(consultant.id),
    }))
  },

  consultantsWithAvailability() {
    return this.consultantsWithNextAvailableDate
      .filter(
        (consultant) =>
          consultant.percentageUtilization > 0 &&
          consultant.employeeType !== EMPLOYEE_TYPE_PARTNER &&
          onAClientOrLastAvailableDayFilter(consultant)
      )
      .map((consultant) => ({
        ...consultant,
        currentStaffingRole: consultant.currentStaffingRoles[0] || {},
        currentClientName: this.getCurrentClientNameByClientId(consultant.currentClientId),
      }))
  },

  consultantsWithTypeOfEmployee() {
    let consultants = []
    for (const id in this.consultants) {
      const consultant = this.consultants[id]
      if (consultant.employeeType === EMPLOYEE_TYPE_EMPLOYEE) {
        consultants = [...consultants, consultant]
      }
    }
    return consultants
  },

  currentUser(state) {
    return state.users[state.currentUserId]
  },

  isAdmin() {
    return this.currentUser?.admin
  },

  unsponsored() {
    const sponsoredConsultantIds = this.sponsorships.map((sponsorship) => sponsorship.protegeId)
    const consultantsToBeUnsponsored = { ...this.consultants }
    sponsoredConsultantIds.forEach((id) => delete consultantsToBeUnsponsored[id])
    return Object.values(consultantsToBeUnsponsored)
  },
}

export const actions = {
  async fetchUsers() {
    const users = await api.fetchUsers()
    if (users.length) {
      this.users = users.reduce((acc, entity) => ({ ...acc, [entity.id]: entity }), {})
    } else {
      this.users = users
    }
  },

  async deleteUser(user) {
    try {
      await api.deleteUser(user)
      const entities = { ...this.users }
      delete entities[user.id]
      this.users = entities
      _surfaceSuccessMessage('User successfully deleted!')
    } catch {
      _surfaceErrorMessage('User could not be deleted!')
    }
  },

  async toggleAdmin() {
    await api.toggleAdmin()
    window.location.reload()
  },

  async logout() {
    await api.logout()
    window.location.replace('login')
  },

  update({ entityName, id, updates }) {
    if (entityName === ENTITIES.USERS) {
      this.users[id] = { ...this.users[id], ...updates }
    } else {
      this[entityName] = this[entityName].map((entity) =>
        entity.id === id ? { ...entity, ...updates } : entity
      )
    }
  },
}
